import { useState, useEffect } from "react"
import {
  useAddPatientIssuer,
  usePatientIssuerInfo,
  useUpdatePatientIssuer,
} from "../../../hooks/usePatientIssuer"
import {
  useGeneralIssuerSettings,
  useUpdateGeneralIssuerSettings,
} from "../../../hooks/useGeneralIssuerSettings"
import Loader from "../../../components/Loader"
import { Form, Formik, Field, ErrorMessage } from "formik"
import { patientIssuerFormSchema } from "../schema"
import TextField from "../../../components/TextField"
import TagInput from "../../../components/TagInput"
import { stringToBoolean } from "../../../utils"
import { getJsonChanges } from "../../../utils/payload"
import ReactDatePicker from "react-datepicker"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarAlt, faInfoCircle } from "@fortawesome/free-solid-svg-icons"

const CreateOrUpdateIssuerFacility = (props) => {
  const { onClose, issuerId, page, limit, sortBy, filters } = props

  const { data, isLoading } = usePatientIssuerInfo(issuerId)
  const { data: generalSettings } = useGeneralIssuerSettings()
  const { mutate: updateGeneralSettings } = useUpdateGeneralIssuerSettings()
  const {
    mutateAsync: addIssuerFacility,
    isPending: isLoadingAddIssuerFacility,
  } = useAddPatientIssuer({
    page,
    limit,
    sortBy,
    filters,
  })
  const {
    mutateAsync: updateIssuerFacility,
    isPending: isLoadingUpdateIssuerFacility,
  } = useUpdatePatientIssuer({ issuerId, page, limit, sortBy, filters })

  const [facilitiesEnabled, setFacilitiesEnabled] = useState(false)

  useEffect(() => {
    setFacilitiesEnabled(!!data?.Facilities)
  }, [data?.Facilities])

  const initialFormValues = {
    IssuerName: data?.IssuerName ?? "",
    isIssuerActive: data?.isIssuerActive ?? true,
    Facilities: data?.Facilities ?? "",
    reportVersion: data?.reportVersion ?? "V3",
    // isReplaceOriginalReport: data?.isReplaceOriginalReport.toString() ?? 'true',
    isReplaceOriginalReport: "true", // we are no longer adding reports to the document section
    isExcludeAIReport: data?.isExcludeAIReport.toString() ?? "false",
    isDefaultReportTitle: data?.isDefaultReportTitle.toString() ?? "true",
    isSendHL7Results: data?.isSendHL7Results.toString() ?? "false",
    isTitleOnTop:
      typeof data?.isTitleOnTop === "boolean"
        ? data?.isTitleOnTop.toString()
        : "true",
    isProcessFinal:
      typeof data?.isProcessFinal === "boolean"
        ? data?.isProcessFinal.toString()
        : "false",
    isQRCodeIncluded: "false",
    isKeyImageDisclaimerEnabled:
      data?.isKeyImageDisclaimerEnabled.toString() ?? "true",
    isV3PhraseReplaced: data?.isV3PhraseReplaced.toString() ?? "true",
    isEnglishDefault: data?.isEnglishDefault.toString() ?? "true",
    isSpanishAISummary: data?.isSpanishAISummary.toString() ?? "false",
    IsPainMapIllustrationOn:
      data?.IsPainMapIllustrationOn.toString() ?? "false",
    isRadiologistProfileQRCode:
      data?.isRadiologistProfileQRCode.toString() ?? "true",
    isEducationalVideosQRCode:
      data?.isEducationalVideosQRCode.toString() ?? "false",
    isAutomationEnabled: data?.isAutomationEnabled.toString() ?? "true",
    isGlossaryIncluded: data?.isGlossaryIncluded.toString() ?? "false",
    qaStopDate: data?.qaStopDate ? new Date(data.qaStopDate) : null,
    customKeywords: data?.customKeywords ?? "",
    globalKeywords: generalSettings?.globalKeywords ?? "",

    // Spine settings
    isSpineOn: data?.spine_issuer_setting?.isSpineOn ?? false,
    // 3D Illustration section
    isSagittal3dCSpine: data?.spine_issuer_setting?.isSagittal3dCSpine ?? false,
    isSagittal3dLSpine: data?.spine_issuer_setting?.isSagittal3dLSpine ?? false,
    isSagittal3dTSpine: data?.spine_issuer_setting?.isSagittal3dTSpine ?? false,
    isAxial3dModelingOn:
      data?.spine_issuer_setting?.isAxial3dModelingOn ?? false,
    // sagittal view
    isSagittalHerniation:
      data?.spine_issuer_setting?.isSagittalHerniation ?? true,
    isSagittalBulges: data?.spine_issuer_setting?.isSagittalBulges ?? false,
    isSagittalSignalLoss:
      data?.spine_issuer_setting?.isSagittalSignalLoss ?? false,
    isSagittalOsteophytes:
      data?.spine_issuer_setting?.isSagittalOsteophytes ?? false,
    isSagittalFissures: data?.spine_issuer_setting?.isSagittalFissures ?? false,
    isSagittalEdemas: data?.spine_issuer_setting?.isSagittalEdemas ?? false,
    isSagittalDeformations:
      data?.spine_issuer_setting?.isSagittalDeformations ?? false,
    isSagittalCompressions:
      data?.spine_issuer_setting?.isSagittalCompressions ?? false,
    isSchmorlsNodes: data?.spine_issuer_setting?.isSchmorlsNodes ?? false,
    // axial view
    isAxialHerniation: data?.spine_issuer_setting?.isAxialHerniation ?? true,
    isAxialBulges: data?.spine_issuer_setting?.isAxialBulges ?? false,
    isAxialArthropathy: data?.spine_issuer_setting?.isAxialArthropathy ?? false,
    isAxialEffusion: data?.spine_issuer_setting?.isAxialEffusion ?? false,

    // Brain settings
    isBrainOn: data?.brain_issuer_setting?.isBrainOn ?? false,
    isWhiteMatterLesions:
      data?.brain_issuer_setting?.isWhiteMatterLesions ?? false,
    isMicroHemorrhages: data?.brain_issuer_setting?.isMicroHemorrhages ?? false,

    // Knee settings
    isKneeOn: data?.knee_issuer_setting?.isKneeOn ?? true,
    // Deep View
    isDeepViewTears: data?.knee_issuer_setting?.isDeepViewTears ?? true,
    isMeniscalTearAxialView:
      data?.knee_issuer_setting?.isMeniscalTearAxialView ?? true,
    isDeepViewOsteophytes: false,
    isChondromalacia: false,
    // Superficial View
    isSpatellaDisplacement: false,
    isSuperficialViewFractures: false,
    isSuperficialViewTears: false,
    isSuperficialViewTendonitis:
      data?.knee_issuer_setting?.isSuperficialViewTendonitis ?? true,
    isAnserineBursitis: false,
    // Lateral View
    isPvns: false,
    isLateralEffusion: data?.knee_issuer_setting?.isLateralEffusion ?? true,
    isRecessCyst: false,
    // Frontal View
    isOcdLesion: false,
    isLooseBody: false,
    isKissingLesion: false,
    isExtrudedMeniscus: false,
    isJointSpaceNarrowing: false,
    isFrontalViewMeniscalTear:
      data?.knee_issuer_setting?.isFrontalViewMeniscalTear ?? true,
    // Sagittal View
    isSagittalViewOsteophytes: false,
    isSagittalViewEffusions:
      data?.knee_issuer_setting?.isSagittalViewEffusions ?? true,
    isSagittalViewTendonitis:
      data?.knee_issuer_setting?.isSagittalViewTendonitis ?? true,
    isSagittalViewTears: false,
    isSagittalViewEdemas: false,
    // Shoulder settings
    isShoulderOn: data?.shoulder_issuer_setting?.isShoulderOn ?? false,
    // Lateral View
    isLateralLabralTears:
      data?.shoulder_issuer_setting?.isLateralLabralTears ?? false,
    isLateralSupraspinatusAtrophy:
      data?.shoulder_issuer_setting?.isLateralSupraspinatusAtrophy ?? false,
    isLateralRcTears: data?.shoulder_issuer_setting?.isLateralRcTears ?? false,
    isLateralTendonosis:
      data?.shoulder_issuer_setting?.isLateralTendonosis ?? false,
    // Frontal View
    isFrontalSupraspinatusAtrophy:
      data?.shoulder_issuer_setting?.isFrontalSupraspinatusAtrophy ?? false,
    isFrontalLabralTears:
      data?.shoulder_issuer_setting?.isFrontalLabralTears ?? false,
    isFrontalRcTears: data?.shoulder_issuer_setting?.isFrontalRcTears ?? false,
    isFrontalTendonosis:
      data?.shoulder_issuer_setting?.isFrontalTendonosis ?? false,
    isFrontalAcJointHypertrophy:
      data?.shoulder_issuer_setting?.isFrontalAcJointHypertrophy ?? false,
    // Superficial View
    isSuperficialSupraspinatusAtrophy:
      data?.shoulder_issuer_setting?.isSuperficialSupraspinatusAtrophy ?? false,
    isSuperficialTendonosisSupraspinatus:
      data?.shoulder_issuer_setting?.isSuperficialTendonosisSupraspinatus ??
      false,
    isSuperficialTendonosisSubscapularis:
      data?.shoulder_issuer_setting?.isSuperficialTendonosisSubscapularis ??
      false,
    isSuperficialRcTearSupraspinatus:
      data?.shoulder_issuer_setting?.isSuperficialRcTearSupraspinatus ?? false,
    isSuperficialRcTearSubscapularis:
      data?.shoulder_issuer_setting?.isSuperficialRcTearSubscapularis ?? false,
    isSuperficialBicepTendonopathy:
      data?.shoulder_issuer_setting?.isSuperficialBicepTendonopathy ?? false,
    isSuperficialBicepTears:
      data?.shoulder_issuer_setting?.isSuperficialBicepTears ?? false,
    isSuperficialAcJointHypertrophy:
      data?.shoulder_issuer_setting?.isSuperficialAcJointHypertrophy ?? false,
  }

  const handleSubmit = async (values) => {
    const isSpineOn = values.reportVersion === "V3" && values.isSpineOn
    const isBrainOn = values.reportVersion === "V3" && values.isBrainOn
    const isKneeOn = values.reportVersion === "V3" && values.isKneeOn
    const isShoulderOn = values.reportVersion === "V3" && values.isShoulderOn

    // Separate global keywords from patient issuer payload
    const globalKeywordsPayload = {
      globalKeywords: values.globalKeywords ?? "",
    }

    const payload = {
      IssuerName: values.IssuerName.trim(),
      isIssuerActive: values.isIssuerActive,
      reportVersion: values.reportVersion?.toUpperCase(),
      isReplaceOriginalReport: stringToBoolean(values.isReplaceOriginalReport),
      isExcludeAIReport:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isExcludeAIReport)
          : false,
      isDefaultReportTitle:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isDefaultReportTitle)
          : true,
      isSendHL7Results: stringToBoolean(values.isSendHL7Results),
      isTitleOnTop: stringToBoolean(values.isTitleOnTop),
      isProcessFinal: stringToBoolean(values.isProcessFinal),
      isQRCodeIncluded:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isQRCodeIncluded)
          : false,
      isKeyImageDisclaimerEnabled:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isKeyImageDisclaimerEnabled)
          : false,
      isV3PhraseReplaced:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isV3PhraseReplaced)
          : false,
      isEnglishDefault:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isEnglishDefault)
          : true,
      isSpanishAISummary:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isSpanishAISummary)
          : false,
      IsPainMapIllustrationOn:
        values.reportVersion === "V3"
          ? stringToBoolean(values.IsPainMapIllustrationOn)
          : false,
      isRadiologistProfileQRCode:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isRadiologistProfileQRCode)
          : false,
      isEducationalVideosQRCode:
        values.reportVersion === "V3"
          ? stringToBoolean(values.isEducationalVideosQRCode)
          : false,
      isAutomationEnabled: stringToBoolean(values.isAutomationEnabled),
      isGlossaryIncluded: stringToBoolean(values.isGlossaryIncluded),
      qaStopDate:
        values.isAutomationEnabled === "true" && values.qaStopDate
          ? new Date(
              Date.UTC(
                values.qaStopDate.getFullYear(),
                values.qaStopDate.getMonth(),
                values.qaStopDate.getDate()
              )
            )
          : null,
      customKeywords: values.customKeywords ?? "",
      Facilities: facilitiesEnabled ? (values.Facilities ?? "") : "",

      // Nested brain settings
      brain_issuer_setting: {
        isBrainOn,
        isWhiteMatterLesions: isBrainOn ? values.isWhiteMatterLesions : false,
        isMicroHemorrhages: isBrainOn ? values.isMicroHemorrhages : false,
      },

      // Nested spine settings
      spine_issuer_setting: {
        isSpineOn,
        // Spine Sagittal View
        isSagittalHerniation: isSpineOn ? values.isSagittalHerniation : false,
        isSagittalBulges: isSpineOn ? values.isSagittalBulges : false,
        isSagittalSignalLoss: isSpineOn ? values.isSagittalSignalLoss : false,
        isSagittalOsteophytes: isSpineOn ? values.isSagittalOsteophytes : false,
        isSagittalFissures: isSpineOn ? values.isSagittalFissures : false,
        isSagittalEdemas: isSpineOn ? values.isSagittalEdemas : false,
        isSagittalDeformations: isSpineOn
          ? values.isSagittalDeformations
          : false,
        isSagittalCompressions: isSpineOn
          ? values.isSagittalCompressions
          : false,
        isSchmorlsNodes: isSpineOn ? values.isSchmorlsNodes : false,
        // Spine Axial View
        isAxialHerniation: isSpineOn ? values?.isAxialHerniation : false,
        isAxialBulges: isSpineOn ? values?.isAxialBulges : false,
        isAxialArthropathy: isSpineOn ? values?.isAxialArthropathy : false,
        isAxialEffusion: isSpineOn ? values?.isAxialEffusion : false,
        // 3D Illustration
        isSagittal3dCSpine: isSpineOn ? values.isSagittal3dCSpine : false,
        isSagittal3dLSpine: isSpineOn ? values.isSagittal3dLSpine : false,
        isSagittal3dTSpine: isSpineOn ? values.isSagittal3dTSpine : false,
        isAxial3dModelingOn: isSpineOn ? values.isAxial3dModelingOn : false,
      },

      // Nested knee settings
      knee_issuer_setting: {
        isKneeOn,
        // Deep View
        isDeepViewTears: isKneeOn ? values.isDeepViewTears : false,
        isMeniscalTearAxialView: isKneeOn
          ? values.isMeniscalTearAxialView
          : false,
        isDeepViewOsteophytes: false,
        isChondromalacia: false,
        // Superficial View
        isSpatellaDisplacement: false,
        isSuperficialViewFractures: false,
        isSuperficialViewTears: false,
        isSuperficialViewTendonitis: isKneeOn
          ? values.isSuperficialViewTendonitis
          : false,
        isAnserineBursitis: false,
        // Lateral View
        isPvns: false,
        isLateralEffusion: isKneeOn ? values.isLateralEffusion : false,
        isRecessCyst: false,
        // Frontal View
        isOcdLesion: false,
        isLooseBody: false,
        isKissingLesion: false,
        isExtrudedMeniscus: false,
        isJointSpaceNarrowing: false,
        isFrontalViewMeniscalTear: isKneeOn
          ? values.isFrontalViewMeniscalTear
          : false,
        // Sagittal View
        isSagittalViewOsteophytes: false,
        isSagittalViewEffusions: isKneeOn
          ? values.isSagittalViewEffusions
          : false,
        isSagittalViewTendonitis: isKneeOn
          ? values.isSagittalViewTendonitis
          : false,
        isSagittalViewTears: false,
        isSagittalViewEdemas: false,
      },

      shoulder_issuer_setting: {
        isShoulderOn,
        isLateralLabralTears: isShoulderOn
          ? values.isLateralLabralTears
          : false,
        isLateralSupraspinatusAtrophy: isShoulderOn
          ? values.isLateralSupraspinatusAtrophy
          : false,
        isLateralRcTears: isShoulderOn ? values.isLateralRcTears : false,
        isLateralTendonosis: isShoulderOn ? values.isLateralTendonosis : false,
        // Frontal View
        isFrontalSupraspinatusAtrophy: isShoulderOn
          ? values.isFrontalSupraspinatusAtrophy
          : false,
        isFrontalLabralTears: isShoulderOn
          ? values.isFrontalLabralTears
          : false,
        isFrontalRcTears: isShoulderOn ? values.isFrontalRcTears : false,
        isFrontalTendonosis: isShoulderOn ? values.isFrontalTendonosis : false,
        isFrontalAcJointHypertrophy: isShoulderOn
          ? values.isFrontalAcJointHypertrophy
          : false,
        // Superficial View
        isSuperficialSupraspinatusAtrophy: isShoulderOn
          ? values.isSuperficialSupraspinatusAtrophy
          : false,
        isSuperficialTendonosisSupraspinatus: isShoulderOn
          ? values.isSuperficialTendonosisSupraspinatus
          : false,
        isSuperficialTendonosisSubscapularis: isShoulderOn
          ? values.isSuperficialTendonosisSubscapularis
          : false,
        isSuperficialRcTearSupraspinatus: isShoulderOn
          ? values.isSuperficialRcTearSupraspinatus
          : false,
        isSuperficialRcTearSubscapularis: isShoulderOn
          ? values.isSuperficialRcTearSubscapularis
          : false,
        isSuperficialBicepTendonopathy: isShoulderOn
          ? values.isSuperficialBicepTendonopathy
          : false,
        isSuperficialBicepTears: isShoulderOn
          ? values.isSuperficialBicepTears
          : false,
        isSuperficialAcJointHypertrophy: isShoulderOn
          ? values.isSuperficialAcJointHypertrophy
          : false,
      },
    }

    // Update global settings if globalKeywords has changed
    if (generalSettings?.globalKeywords !== values.globalKeywords) {
      await updateGeneralSettings(globalKeywordsPayload)
    }

    if (issuerId && data.PatientIssuerId === issuerId) {
      const editPayload = getJsonChanges(data, payload)
      await updateIssuerFacility({
        issuerId,
        ...editPayload,
      })
      onClose()
    } else {
      await addIssuerFacility(payload)
      onClose()
    }
  }

  return (
    <>
      <div className="p-2 flex justify-between items-center">
        <strong className="text-lg">
          {issuerId ? "Update Patient Issuer" : "Add Patient Issuer"}
        </strong>
        <button
          className="text-gray-600 hover:text-gray-900 text-3xl ml-auto"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="max-h-[80vh] overflow-y-auto p-2">
        {issuerId && isLoading ? (
          <div className="py-2">
            <Loader />
          </div>
        ) : (
          <div>
            <Formik
              enableReinitialize
              initialValues={initialFormValues}
              validationSchema={patientIssuerFormSchema}
              onSubmit={handleSubmit}
            >
              {({ handleChange, values, setFieldValue }) => (
                <Form>
                  <div className="flex gap-6">
                    <div className="w-6/12">
                      <TextField
                        label="Patient Issuer Name"
                        name="IssuerName"
                        placeholder="Enter Patient Issuer Name"
                      />
                    </div>
                    <div className="w-6/12 ml-14 flex items-center gap-2 mt-3">
                      <label
                        className="flex gap-3 items-center cursor-pointer"
                        htmlFor="toggle"
                      >
                        <div className="ml-2 text-gray-700 font-medium text-nowrap">
                          Is Rule Active?
                        </div>
                        <div className="relative">
                          <Field
                            checked={values.isIssuerActive}
                            className="sr-only peer"
                            id="toggle"
                            name="isIssuerActive"
                            type="checkbox"
                            onChange={handleChange}
                          />
                          <div className="block bg-gray-300 w-10 h-6 rounded-full peer-checked:bg-secondary"></div>
                          <div className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-full"></div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="w-full h-[1px] bg-gray-200 mt-6"></div>

                  <div className="mt-4">
                    <div className="flex items-center gap-12">
                      <h3 className="text-xl font-semibold">Facilities</h3>
                      <ToggleSwitch
                        checked={facilitiesEnabled}
                        label=""
                        name="facilitiesEnabled"
                        onChange={() => {
                          const next = !facilitiesEnabled
                          setFacilitiesEnabled(next)
                          if (!next) setFieldValue("Facilities", "")
                        }}
                      />
                    </div>
                    {facilitiesEnabled && (
                      <div className="mt-3 w-6/12">
                        <TagInput
                          name="Facilities"
                          placeholder="Add Facility Name"
                          setFieldValue={setFieldValue}
                          value={values.Facilities}
                        />
                      </div>
                    )}
                  </div>

                  <div className="w-full h-[1px] bg-gray-200 mt-3"></div>

                  <div className="flex gap-6 mt-8">
                    <p className="w-3/12 text-gray-500 text-md font-semibold">
                      Get FINAL results via HL7?
                    </p>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isSendHL7Results"
                        type="radio"
                        value="true"
                      />
                      <div className="font-medium text-nowrap">Yes</div>
                    </label>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isSendHL7Results"
                        type="radio"
                        value="false"
                      />
                      <div className="font-medium text-nowrap">No</div>
                    </label>
                  </div>

                  <div className="flex gap-6 mt-4">
                    <p className="w-3/12 text-gray-500 text-md font-semibold">
                      Process final report for V3 features
                    </p>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isProcessFinal"
                        type="radio"
                        value="true"
                      />
                      <div className="font-medium text-nowrap">Yes</div>
                    </label>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isProcessFinal"
                        type="radio"
                        value="false"
                      />
                      <div className="font-medium text-nowrap">No</div>
                    </label>
                  </div>

                  <div className="flex gap-6 mt-4">
                    <p className="w-3/12 text-gray-500 text-md font-semibold">
                      Is Title On Top?
                    </p>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isTitleOnTop"
                        type="radio"
                        value="true"
                      />
                      <div className="font-medium text-nowrap">Yes</div>
                    </label>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isTitleOnTop"
                        type="radio"
                        value="false"
                      />
                      <div className="font-medium text-nowrap">No</div>
                    </label>
                  </div>

                  <div className="flex gap-6 mt-4">
                    <p className="w-3/12 text-gray-500 text-md font-semibold">
                      Report version to use?
                    </p>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="reportVersion"
                        type="radio"
                        value="V3"
                      />
                      <div className="font-medium text-nowrap">V3 Reports</div>
                    </label>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="reportVersion"
                        type="radio"
                        value="V2"
                      />
                      <div className="font-medium text-nowrap">V2 Reports</div>
                    </label>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="reportVersion"
                        type="radio"
                        value="ORIGINAL"
                      />
                      <div className="font-medium text-nowrap">
                        Original Reports
                      </div>
                    </label>
                  </div>

                  {values.reportVersion === "V3" && (
                    <div>
                      <div className="w-full h-[1px] bg-gray-200 mt-3"></div>

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Access to V3 Features?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isExcludeAIReport"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">
                            All V3 features
                          </div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isExcludeAIReport"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">
                            Exclude AI Report
                          </div>
                        </label>
                      </div>

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Use default report title?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isDefaultReportTitle"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isDefaultReportTitle"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div>

                      {/* <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          QR Code Included?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isQRCodeIncluded"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isQRCodeIncluded"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div> */}

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Key Image Disclaimer included?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isKeyImageDisclaimerEnabled"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isKeyImageDisclaimerEnabled"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div>

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Show Extended Thank you Text
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isV3PhraseReplaced"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isV3PhraseReplaced"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div>

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Show Pain Map for C-Spine Reports?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="IsPainMapIllustrationOn"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="IsPainMapIllustrationOn"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div>

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Attach Radiologist CV QR Code?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isRadiologistProfileQRCode"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isRadiologistProfileQRCode"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div>
                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Attach Educational videos QR Code?
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isEducationalVideosQRCode"
                            type="radio"
                            value="true"
                          />
                          <div className="font-medium text-nowrap">Yes</div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isEducationalVideosQRCode"
                            type="radio"
                            value="false"
                          />
                          <div className="font-medium text-nowrap">No</div>
                        </label>
                      </div>
                    </div>
                  )}

                  {(values.reportVersion === "V3" ||
                    values.reportVersion === "V2") && (
                    <div
                      className={`flex gap-6 mt-4 ${values.reportVersion === "V2" && "border-t pt-7"}`}
                    >
                      <p className="w-3/12 text-gray-500 text-md font-semibold">
                        Include Patient-Friendly Glossary?
                      </p>
                      <label className="flex gap-3 items-center cursor-pointer">
                        <Field
                          className="w-6 h-6 text-blue-600 form-radio"
                          name="isGlossaryIncluded"
                          type="radio"
                          value="true"
                        />
                        <div className="font-medium text-nowrap">Yes</div>
                      </label>
                      <label className="flex gap-3 items-center cursor-pointer">
                        <Field
                          className="w-6 h-6 text-blue-600 form-radio"
                          name="isGlossaryIncluded"
                          type="radio"
                          value="false"
                        />
                        <div className="font-medium text-nowrap">No</div>
                      </label>
                    </div>
                  )}
                  {values.reportVersion === "V3" && (
                    <>
                      <div className="w-full h-[1px] bg-gray-200 mt-6 mb-4"></div>
                      <h3 className="text-xl font-semibold mb-4">
                        Language Specific Settings
                      </h3>

                      <div className="flex gap-6 mt-4">
                        <p className="w-3/12 text-gray-500 text-md font-semibold">
                          Language Preference
                        </p>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isEnglishDefault"
                            type="radio"
                            value="true"
                            onClick={() => {
                              setFieldValue("isSpanishAISummary", "false")
                            }}
                          />
                          <div className="font-medium text-nowrap">
                            English (Default)
                          </div>
                        </label>
                        <label className="flex gap-3 items-center cursor-pointer">
                          <Field
                            className="w-6 h-6 text-blue-600 form-radio"
                            name="isEnglishDefault"
                            type="radio"
                            value="false"
                            onClick={() => {
                              setFieldValue("isSpanishAISummary", "true")
                            }}
                          />
                          <div className="font-medium text-nowrap">
                            Spanish (For Simplified Patient Explanation Only)
                          </div>
                        </label>
                      </div>
                    </>
                  )}

                  <div className="w-full h-[1px] bg-gray-200 mt-6 mb-4"></div>
                  <h3 className="text-xl font-semibold mb-4">
                    Report Re-Routing Settings
                  </h3>

                  <div className="flex gap-6 mt-4">
                    <p className="w-3/12 text-gray-500 text-md font-semibold">
                      Is Report Re-Routing Enabled?
                    </p>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isAutomationEnabled"
                        type="radio"
                        value="true"
                      />
                      <div className="font-medium text-nowrap">Yes</div>
                    </label>
                    <label className="flex gap-3 items-center cursor-pointer">
                      <Field
                        className="w-6 h-6 text-blue-600 form-radio"
                        name="isAutomationEnabled"
                        type="radio"
                        value="false"
                      />
                      <div className="font-medium text-nowrap">No</div>
                    </label>
                  </div>
                  {values.isAutomationEnabled === "true" && (
                    <>
                      <div className="flex gap-6 mt-4">
                        <div className="w-3/12 flex items-center gap-2">
                          <p className="text-gray-500 text-md font-semibold">
                            QA-Stop Date
                          </p>
                          <div className="relative group">
                            <FontAwesomeIcon
                              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help"
                              icon={faInfoCircle}
                            />
                            <div className="absolute left-0 bottom-6 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                              Before this date, Normal or Only bulge reports are
                              automatically routed for a second look
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="border p-1 rounded-md w-[160px] cursor-pointer">
                            <ReactDatePicker
                              scrollableYearDropdown
                              showIcon
                              showMonthDropdown
                              showYearDropdown
                              className="outline-none bg-transparent px-1 h-6 text-[12px] no-spinner cursor-pointer ml-1"
                              icon={
                                <FontAwesomeIcon
                                  className="!w-[13px] !h-[13px] cursor-pointer -translate-y-[1.2px]"
                                  icon={faCalendarAlt}
                                />
                              }
                              placeholderText={"Select QA-Stop Date"}
                              selected={
                                values.qaStopDate
                                  ? new Date(values.qaStopDate)
                                  : null
                              }
                              yearDropdownItemNumber={80}
                              onChange={(date) =>
                                setFieldValue("qaStopDate", date)
                              }
                              {...props}
                            />
                          </div>
                          <ErrorMessage name="qaStopDate">
                            {(msg) => (
                              <p className="text-rose-500 text-xs ml-2 mt-1">
                                {msg}
                              </p>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>
                      <div className="flex gap-6 mt-4">
                        <div className="w-3/12 flex gap-2">
                          <p className="text-gray-500 text-md font-semibold">
                            Custom Triggers
                          </p>
                          <div className="relative group">
                            <FontAwesomeIcon
                              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help"
                              icon={faInfoCircle}
                            />
                            <div className="absolute left-0 bottom-6 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                              After this date, reports follow the normal
                              workflow, which includes any custom keyword
                              triggers routed for a second look
                            </div>
                          </div>
                        </div>
                        <div className="w-6/12">
                          <TagInput
                            name="customKeywords"
                            placeholder="Add Custom Keywords"
                            setFieldValue={setFieldValue}
                            value={values.customKeywords}
                          />
                        </div>
                      </div>
                      <div className="flex gap-6 mt-4">
                        <div className="w-3/12 flex gap-2">
                          <p className="text-gray-500 text-md font-semibold">
                            Global Triggers
                          </p>
                          <div className="relative group">
                            <FontAwesomeIcon
                              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help"
                              icon={faInfoCircle}
                            />
                            <div className="absolute left-0 bottom-6 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                              After this date, reports follow the normal
                              workflow, which includes any global keyword
                              triggers routed for a second look
                            </div>
                          </div>
                        </div>
                        <div className="w-6/12">
                          <TagInput
                            name="globalKeywords"
                            placeholder="Add Global Keywords"
                            setFieldValue={setFieldValue}
                            value={values.globalKeywords}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {values.reportVersion === "V3" && (
                    <>
                      <div className="w-full h-[1px] bg-gray-200 mt-6 mb-4"></div>
                      <h3 className="text-xl font-semibold mb-4">
                        Illustrations Specific Settings
                      </h3>

                      <div className="border rounded-lg mb-4 overflow-hidden">
                        <div className="border-b p-4 flex items-center">
                          <div className="mr-4">
                            <ToggleSwitch
                              checked={values.isSpineOn}
                              label="Spine"
                              name="isSpineOn"
                              onChange={() =>
                                setFieldValue("isSpineOn", !values.isSpineOn)
                              }
                            />
                          </div>
                        </div>

                        {values.isSpineOn ? (
                          <>
                            {/* Sagittal View */}
                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Sagittal View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isSagittalHerniation",
                                        true
                                      )
                                      setFieldValue("isSagittalBulges", true)
                                      setFieldValue(
                                        "isSagittalSignalLoss",
                                        true
                                      )
                                      setFieldValue(
                                        "isSagittalOsteophytes",
                                        true
                                      )
                                      setFieldValue("isSagittalFissures", true)
                                      setFieldValue("isSagittalEdemas", true)
                                      setFieldValue(
                                        "isSagittalDeformations",
                                        true
                                      )
                                      setFieldValue(
                                        "isSagittalCompressions",
                                        true
                                      )
                                      setFieldValue("isSchmorlsNodes", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isSagittalHerniation",
                                        false
                                      )
                                      setFieldValue("isSagittalBulges", false)
                                      setFieldValue(
                                        "isSagittalSignalLoss",
                                        false
                                      )
                                      setFieldValue(
                                        "isSagittalOsteophytes",
                                        false
                                      )
                                      setFieldValue("isSagittalFissures", false)
                                      setFieldValue("isSagittalEdemas", false)
                                      setFieldValue(
                                        "isSagittalDeformations",
                                        false
                                      )
                                      setFieldValue(
                                        "isSagittalCompressions",
                                        false
                                      )
                                      setFieldValue("isSchmorlsNodes", false)
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                <Checkbox
                                  checked={values.isSagittalHerniation}
                                  label="Herniation"
                                  name="isSagittalHerniation"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalHerniation",
                                      !values.isSagittalHerniation
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalBulges}
                                  label="Bulges"
                                  name="isSagittalBulges"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalBulges",
                                      !values.isSagittalBulges
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalSignalLoss}
                                  label="Signal Loss"
                                  name="isSagittalSignalLoss"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalSignalLoss",
                                      !values.isSagittalSignalLoss
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalOsteophytes}
                                  label="Osteophytes"
                                  name="isSagittalOsteophytes"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalOsteophytes",
                                      !values.isSagittalOsteophytes
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalFissures}
                                  label="Fissures"
                                  name="isSagittalFissures"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalFissures",
                                      !values.isSagittalFissures
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalEdemas}
                                  label="Edemas"
                                  name="isSagittalEdemas"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalEdemas",
                                      !values.isSagittalEdemas
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalDeformations}
                                  label="Deformations"
                                  name="isSagittalDeformations"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalDeformations",
                                      !values.isSagittalDeformations
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalCompressions}
                                  label="Compressions"
                                  name="isSagittalCompressions"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalCompressions",
                                      !values.isSagittalCompressions
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSchmorlsNodes}
                                  label="Schmorl's Nodes"
                                  name="isSchmorlsNodes"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSchmorlsNodes",
                                      !values.isSchmorlsNodes
                                    )
                                  }
                                />
                              </div>
                            </div>

                            {/* Axial View */}
                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Axial View</h4>
                                <div className="flex gap-2">
                                  {/* <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue("isAxialHerniation", true)
                                      setFieldValue("isAxialBulges", true)
                                      setFieldValue("isAxialArthropathy", true)
                                      setFieldValue("isAxialEffusion", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue("isAxialHerniation", false)
                                      setFieldValue("isAxialBulges", false)
                                      setFieldValue("isAxialArthropathy", false)
                                      setFieldValue("isAxialEffusion", false)
                                    }}
                                  >
                                    Disable All
                                  </button> */}
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                <Checkbox
                                  checked={values.isAxialHerniation}
                                  label="Herniation"
                                  name="isAxialHerniation"
                                  onChange={() =>
                                    setFieldValue(
                                      "isAxialHerniation",
                                      !values.isAxialHerniation
                                    )
                                  }
                                />

                                {/* <Checkbox
                                  checked={values.isAxialBulges}
                                  label="Bulges"
                                  name="isAxialBulges"
                                  onChange={() =>
                                    setFieldValue(
                                      "isAxialBulges",
                                      !values.isAxialBulges
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isAxialArthropathy}
                                  label="Arthropathy"
                                  name="isAxialArthropathy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isAxialArthropathy",
                                      !values.isAxialArthropathy
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isAxialEffusion}
                                  label="Effusion"
                                  name="isAxialEffusion"
                                  onChange={() =>
                                    setFieldValue(
                                      "isAxialEffusion",
                                      !values.isAxialEffusion
                                    )
                                  }
                                /> */}
                              </div>
                            </div>

                            {/* 3D illustration View */}
                            <div className="p-4">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">3D Illustration</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue("isSagittal3dCSpine", true)
                                      setFieldValue("isSagittal3dLSpine", true)
                                      setFieldValue("isSagittal3dTSpine", true)
                                      // todo: to be enabled afterwards
                                      // setFieldValue("isAxial3dModelingOn", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue("isSagittal3dCSpine", false)
                                      setFieldValue("isSagittal3dLSpine", false)
                                      setFieldValue("isSagittal3dTSpine", false)
                                      // todo: to be enabled afterwards
                                      // setFieldValue(
                                      //   "isAxial3dModelingOn",
                                      //   false
                                      // )
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-4">
                                <Checkbox
                                  checked={values.isSagittal3dCSpine}
                                  label="C-Spine"
                                  name="isSagittal3dCSpine"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittal3dCSpine",
                                      !values.isSagittal3dCSpine
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittal3dTSpine}
                                  label="T-Spine"
                                  name="isSagittal3dTSpine"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittal3dTSpine",
                                      !values.isSagittal3dTSpine
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittal3dLSpine}
                                  label="L-Spine"
                                  name="isSagittal3dLSpine"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittal3dLSpine",
                                      !values.isSagittal3dLSpine
                                    )
                                  }
                                />

                                {/* todo: to be enabled afterwards*/}
                                {/* <Checkbox
                                  checked={values.isAxial3dModelingOn}
                                  label="Axial View"
                                  name="isAxial3dModelingOn"
                                  onChange={() =>
                                    setFieldValue(
                                      "isAxial3dModelingOn",
                                      !values.isAxial3dModelingOn
                                    )
                                  }
                                /> */}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="p-6 text-center text-gray-500 italic">
                            Enable this body part to configure settings
                          </div>
                        )}
                      </div>

                      {/* Brain Section */}
                      <div className="border rounded-lg mb-4 overflow-hidden">
                        <div className="border-b p-4 flex items-center">
                          <div className="mr-4">
                            <ToggleSwitch
                              checked={values.isBrainOn}
                              label="Brain"
                              name="isBrainOn"
                              onChange={(e) => {
                                setFieldValue("isBrainOn", !values.isBrainOn)
                              }}
                            />
                          </div>
                        </div>

                        {values.isBrainOn ? (
                          <div className="p-4">
                            <div className="flex justify-between items-center mb-6">
                              <h4 className="text-lg font-medium">
                                Brain Settings
                              </h4>
                              <div className="flex gap-2">
                                <button
                                  className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                  type="button"
                                  onClick={() => {
                                    setFieldValue("isWhiteMatterLesions", true)
                                    setFieldValue("isMicroHemorrhages", true)
                                  }}
                                >
                                  Enable All
                                </button>
                                <button
                                  className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                  type="button"
                                  onClick={() => {
                                    setFieldValue("isWhiteMatterLesions", false)
                                    setFieldValue("isMicroHemorrhages", false)
                                  }}
                                >
                                  Disable All
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                              <Checkbox
                                checked={values.isWhiteMatterLesions}
                                label="White Matter Lesions"
                                name="isWhiteMatterLesions"
                                onChange={() =>
                                  setFieldValue(
                                    "isWhiteMatterLesions",
                                    !values.isWhiteMatterLesions
                                  )
                                }
                              />

                              <Checkbox
                                checked={values.isMicroHemorrhages}
                                label="Micro Hemorrhages"
                                name="isMicroHemorrhages"
                                onChange={() =>
                                  setFieldValue(
                                    "isMicroHemorrhages",
                                    !values.isMicroHemorrhages
                                  )
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center text-gray-500 italic">
                            Enable this body part to configure settings
                          </div>
                        )}
                      </div>

                      {/* Knee Section */}
                      <div className="border rounded-lg mb-4 overflow-hidden">
                        <div className="border-b p-4 flex items-center">
                          <div className="mr-4">
                            <ToggleSwitch
                              checked={values.isKneeOn}
                              label="Knee"
                              name="isKneeOn"
                              onChange={(e) => {
                                setFieldValue("isKneeOn", !values.isKneeOn)
                              }}
                            />
                          </div>
                        </div>

                        {values.isKneeOn ? (
                          <>
                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Deep View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue("isDeepViewTears", true)
                                      setFieldValue(
                                        "isMeniscalTearAxialView",
                                        true
                                      )
                                      // setFieldValue(
                                      //   "isDeepViewOsteophytes",
                                      //   true
                                      // )
                                      // setFieldValue("isChondromalacia", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue("isDeepViewTears", false)
                                      setFieldValue(
                                        "isMeniscalTearAxialView",
                                        false
                                      )
                                      // setFieldValue(
                                      //   "isDeepViewOsteophytes",
                                      //   false
                                      // )
                                      // setFieldValue("isChondromalacia", false)
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>
                              <div className="my-2 grid grid-cols-4 gap-4">
                                <Checkbox
                                  checked={values.isDeepViewTears}
                                  label="Ligament Tears"
                                  name="isDeepViewTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isDeepViewTears",
                                      !values.isDeepViewTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isMeniscalTearAxialView}
                                  label="Meniscal Tear"
                                  name="isMeniscalTearAxialView"
                                  onChange={() =>
                                    setFieldValue(
                                      "isMeniscalTearAxialView",
                                      !values.isMeniscalTearAxialView
                                    )
                                  }
                                />

                                {/* <Checkbox
                                  checked={values.isDeepViewOsteophytes}
                                  label="Osteophytes"
                                  name="isDeepViewOsteophytes"
                                  onChange={() =>
                                    setFieldValue(
                                      "isDeepViewOsteophytes",
                                      !values.isDeepViewOsteophytes
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isChondromalacia}
                                  label="Chondromalacia"
                                  name="isChondromalacia"
                                  onChange={() =>
                                    setFieldValue(
                                      "isChondromalacia",
                                      !values.isChondromalacia
                                    )
                                  }
                                />*/}
                              </div>
                            </div>

                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Frontal View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue("isOcdLesion", true)
                                      // setFieldValue("isLooseBody", true)
                                      // setFieldValue("isKissingLesion", true)
                                      // setFieldValue("isExtrudedMeniscus", true)
                                      // setFieldValue(
                                      //   "isJointSpaceNarrowing",
                                      //   true
                                      // )
                                      setFieldValue(
                                        "isFrontalViewMeniscalTear",
                                        true
                                      )
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue("isOcdLesion", false)
                                      // setFieldValue("isLooseBody", false)
                                      // setFieldValue("isKissingLesion", false)
                                      // setFieldValue("isExtrudedMeniscus", false)
                                      // setFieldValue(
                                      //   "isJointSpaceNarrowing",
                                      //   false
                                      // )
                                      setFieldValue(
                                        "isFrontalViewMeniscalTear",
                                        false
                                      )
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-3 gap-4">
                                {/* <Checkbox
                                  checked={values.isOcdLesion}
                                  label="OCD Lesion"
                                  name="isOcdLesion"
                                  onChange={() =>
                                    setFieldValue(
                                      "isOcdLesion",
                                      !values.isOcdLesion
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isLooseBody}
                                  label="Loose Body"
                                  name="isLooseBody"
                                  onChange={() =>
                                    setFieldValue(
                                      "isLooseBody",
                                      !values.isLooseBody
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isKissingLesion}
                                  label="Kissing Lesion"
                                  name="isKissingLesion"
                                  onChange={() =>
                                    setFieldValue(
                                      "isKissingLesion",
                                      !values.isKissingLesion
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isExtrudedMeniscus}
                                  label="Extruded Meniscus"
                                  name="isExtrudedMeniscus"
                                  onChange={() =>
                                    setFieldValue(
                                      "isExtrudedMeniscus",
                                      !values.isExtrudedMeniscus
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isJointSpaceNarrowing}
                                  label="Joint Space Narrowing"
                                  name="isJointSpaceNarrowing"
                                  onChange={() =>
                                    setFieldValue(
                                      "isJointSpaceNarrowing",
                                      !values.isJointSpaceNarrowing
                                    )
                                  }
                                /> */}

                                <Checkbox
                                  checked={values.isFrontalViewMeniscalTear}
                                  label="Meniscal Tear"
                                  name="isFrontalViewMeniscalTear"
                                  onChange={() =>
                                    setFieldValue(
                                      "isFrontalViewMeniscalTear",
                                      !values.isFrontalViewMeniscalTear
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">
                                  Superficial View
                                </h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue(
                                      //   "isSuperficialViewFractures",
                                      //   true
                                      // )
                                      // setFieldValue(
                                      //   "isSuperficialViewTears",
                                      //   true
                                      // )
                                      setFieldValue(
                                        "isSuperficialViewTendonitis",
                                        true
                                      )
                                      // setFieldValue(
                                      //   "isSpatellaDisplacement",
                                      //   true
                                      // )
                                      // setFieldValue("isAnserineBursitis", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue(
                                      //   "isSuperficialViewFractures",
                                      //   false
                                      // )
                                      // setFieldValue(
                                      //   "isSuperficialViewTears",
                                      //   false
                                      // )
                                      setFieldValue(
                                        "isSuperficialViewTendonitis",
                                        false
                                      )
                                      // setFieldValue(
                                      //   "isSpatellaDisplacement",
                                      //   false
                                      // )
                                      // setFieldValue("isAnserineBursitis", false)
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                {/* <Checkbox
                                  checked={values.isSpatellaDisplacement}
                                  label="Spatella Displacement"
                                  name="isSpatellaDisplacement"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSpatellaDisplacement",
                                      !values.isSpatellaDisplacement
                                    )
                                  }
                                />
                                <Checkbox
                                  checked={values.isSuperficialViewFractures}
                                  label="Fractures"
                                  name="isSuperficialViewFractures"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialViewFractures",
                                      !values.isSuperficialViewFractures
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSuperficialViewTears}
                                  label="Tears"
                                  name="isSuperficialViewTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialViewTears",
                                      !values.isSuperficialViewTears
                                    )
                                  }
                                /> */}

                                <Checkbox
                                  checked={values.isSuperficialViewTendonitis}
                                  label="Tendonitis"
                                  name="isSuperficialViewTendonitis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialViewTendonitis",
                                      !values.isSuperficialViewTendonitis
                                    )
                                  }
                                />
                                {/* <Checkbox
                                  checked={values.isAnserineBursitis}
                                  label="Anserine Bursitis"
                                  name="isAnserineBursitis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isAnserineBursitis",
                                      !values.isAnserineBursitis
                                    )
                                  }
                                /> */}
                              </div>
                            </div>

                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Sagittal View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue(
                                      //   "isSagittalViewOsteophytes",
                                      //   true
                                      // )
                                      setFieldValue(
                                        "isSagittalViewEffusions",
                                        true
                                      )
                                      setFieldValue(
                                        "isSagittalViewTendonitis",
                                        true
                                      )
                                      // setFieldValue("isSagittalViewTears", true)
                                      // setFieldValue(
                                      //   "isSagittalViewEdemas",
                                      //   true
                                      // )
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue(
                                      //   "isSagittalViewOsteophytes",
                                      //   false
                                      // )
                                      setFieldValue(
                                        "isSagittalViewEffusions",
                                        false
                                      )
                                      setFieldValue(
                                        "isSagittalViewTendonitis",
                                        false
                                      )
                                      // setFieldValue(
                                      //   "isSagittalViewTears",
                                      //   false
                                      // )
                                      // setFieldValue(
                                      //   "isSagittalViewEdemas",
                                      //   false
                                      // )
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                {/* <Checkbox
                                  checked={values.isSagittalViewOsteophytes}
                                  label="Osteophytes"
                                  name="isSagittalViewOsteophytes"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalViewOsteophytes",
                                      !values.isSagittalViewOsteophytes
                                    )
                                  }
                                /> */}

                                <Checkbox
                                  checked={values.isSagittalViewEffusions}
                                  label="Patellar Effusions"
                                  name="isSagittalViewEffusions"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalViewEffusions",
                                      !values.isSagittalViewEffusions
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalViewTendonitis}
                                  label="Tendonitis"
                                  name="isSagittalViewTendonitis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalViewTendonitis",
                                      !values.isSagittalViewTendonitis
                                    )
                                  }
                                />

                                {/* <Checkbox
                                  checked={values.isSagittalViewTears}
                                  label="Tears"
                                  name="isSagittalViewTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalViewTears",
                                      !values.isSagittalViewTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSagittalViewEdemas}
                                  label="Edemas"
                                  name="isSagittalViewEdemas"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSagittalViewEdemas",
                                      !values.isSagittalViewEdemas
                                    )
                                  }
                                /> */}
                              </div>
                            </div>

                            <div className="p-4 ">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Lateral View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue("isPvns", true)
                                      setFieldValue("isLateralEffusion", true)
                                      // setFieldValue("isRecessCyst", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      // setFieldValue("isPvns", false)
                                      setFieldValue("isLateralEffusion", false)
                                      // setFieldValue("isRecessCyst", false)
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-3 gap-4">
                                {/* <Checkbox
                                  checked={values.isPvns}
                                  label="PVNS"
                                  name="isPvns"
                                  onChange={() =>
                                    setFieldValue("isPvns", !values.isPvns)
                                  }
                                />*/}

                                <Checkbox
                                  checked={values.isLateralEffusion}
                                  label="Joint Effusion"
                                  name="isLateralEffusion"
                                  onChange={() =>
                                    setFieldValue(
                                      "isLateralEffusion",
                                      !values.isLateralEffusion
                                    )
                                  }
                                />

                                {/* <Checkbox
                                  checked={values.isRecessCyst}
                                  label="Recess Cyst"
                                  name="isRecessCyst"
                                  onChange={() =>
                                    setFieldValue(
                                      "isRecessCyst",
                                      !values.isRecessCyst
                                    )
                                  }
                                /> */}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="p-6 text-center text-gray-500 italic">
                            Enable this body part to configure settings
                          </div>
                        )}
                      </div>

                      {/* Shoulder Section */}
                      <div className="border rounded-lg mb-4 overflow-hidden">
                        <div className="border-b p-4 flex items-center">
                          <div className="mr-4">
                            <ToggleSwitch
                              checked={values.isShoulderOn}
                              label="Shoulder"
                              name="isShoulderOn"
                              onChange={(e) => {
                                setFieldValue(
                                  "isShoulderOn",
                                  !values.isShoulderOn
                                )
                              }}
                            />
                          </div>
                        </div>

                        {values.isShoulderOn ? (
                          <>
                            <div className="p-4 border-b">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Lateral View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isLateralLabralTears",
                                        true
                                      )
                                      setFieldValue(
                                        "isLateralSupraspinatusAtrophy",
                                        true
                                      )
                                      setFieldValue("isLateralRcTears", true)
                                      setFieldValue("isLateralTendonosis", true)
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isLateralLabralTears",
                                        false
                                      )
                                      setFieldValue(
                                        "isLateralSupraspinatusAtrophy",
                                        false
                                      )
                                      setFieldValue("isLateralRcTears", false)
                                      setFieldValue(
                                        "isLateralTendonosis",
                                        false
                                      )
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                <Checkbox
                                  checked={values.isLateralLabralTears}
                                  label="Labral Tear"
                                  name="isLateralLabralTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isLateralLabralTears",
                                      !values.isLateralLabralTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isLateralSupraspinatusAtrophy}
                                  label="Atrophy"
                                  name="isLateralSupraspinatusAtrophy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isLateralSupraspinatusAtrophy",
                                      !values.isLateralSupraspinatusAtrophy
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isLateralRcTears}
                                  label="Rotator Cuff Tear"
                                  name="isLateralRcTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isLateralRcTears",
                                      !values.isLateralRcTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isLateralTendonosis}
                                  label="Tendonosis"
                                  name="isLateralTendonosis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isLateralTendonosis",
                                      !values.isLateralTendonosis
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div className="p-4">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">Frontal View</h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isFrontalSupraspinatusAtrophy",
                                        true
                                      )
                                      setFieldValue(
                                        "isFrontalLabralTears",
                                        true
                                      )
                                      setFieldValue("isFrontalRcTears", true)
                                      setFieldValue("isFrontalTendonosis", true)
                                      setFieldValue(
                                        "isFrontalAcJointHypertrophy",
                                        true
                                      )
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isFrontalSupraspinatusAtrophy",
                                        false
                                      )
                                      setFieldValue(
                                        "isFrontalLabralTears",
                                        false
                                      )
                                      setFieldValue("isFrontalRcTears", false)
                                      setFieldValue(
                                        "isFrontalTendonosis",
                                        false
                                      )
                                      setFieldValue(
                                        "isFrontalAcJointHypertrophy",
                                        false
                                      )
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                <Checkbox
                                  checked={values.isFrontalLabralTears}
                                  label="Labral Tear"
                                  name="isFrontalLabralTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isFrontalLabralTears",
                                      !values.isFrontalLabralTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isFrontalSupraspinatusAtrophy}
                                  label="Atrophy"
                                  name="isFrontalSupraspinatusAtrophy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isFrontalSupraspinatusAtrophy",
                                      !values.isFrontalSupraspinatusAtrophy
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isFrontalRcTears}
                                  label="Rotator Cuff Tear"
                                  name="isFrontalRcTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isFrontalRcTears",
                                      !values.isFrontalRcTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isFrontalTendonosis}
                                  label="Tendonosis"
                                  name="isFrontalTendonosis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isFrontalTendonosis",
                                      !values.isFrontalTendonosis
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isFrontalAcJointHypertrophy}
                                  label="AC Joint Hypertrophy"
                                  name="isFrontalAcJointHypertrophy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isFrontalAcJointHypertrophy",
                                      !values.isFrontalAcJointHypertrophy
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div className="p-4 border-t">
                              <div className="flex justify-between items-center mb-6">
                                <h4 className="font-medium">
                                  Superficial View
                                </h4>
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isSuperficialSupraspinatusAtrophy",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialTendonosisSupraspinatus",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialTendonosisSubscapularis",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialRcTearSupraspinatus",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialRcTearSubscapularis",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialBicepTendonopathy",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialBicepTears",
                                        true
                                      )
                                      setFieldValue(
                                        "isSuperficialAcJointHypertrophy",
                                        true
                                      )
                                    }}
                                  >
                                    Enable All
                                  </button>
                                  <button
                                    className="px-4 py-2 border text-sm rounded-md bg-white hover:bg-gray-50"
                                    type="button"
                                    onClick={() => {
                                      setFieldValue(
                                        "isSuperficialSupraspinatusAtrophy",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialTendonosisSupraspinatus",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialTendonosisSubscapularis",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialRcTearSupraspinatus",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialRcTearSubscapularis",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialBicepTendonopathy",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialBicepTears",
                                        false
                                      )
                                      setFieldValue(
                                        "isSuperficialAcJointHypertrophy",
                                        false
                                      )
                                    }}
                                  >
                                    Disable All
                                  </button>
                                </div>
                              </div>

                              <div className="my-2 grid grid-cols-4 gap-4">
                                <Checkbox
                                  checked={
                                    values.isSuperficialSupraspinatusAtrophy
                                  }
                                  label="Supraspinatus Atrophy"
                                  name="isSuperficialSupraspinatusAtrophy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialSupraspinatusAtrophy",
                                      !values.isSuperficialSupraspinatusAtrophy
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={
                                    values.isSuperficialTendonosisSupraspinatus
                                  }
                                  label="Tendonosis Supraspinatus"
                                  name="isSuperficialTendonosisSupraspinatus"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialTendonosisSupraspinatus",
                                      !values.isSuperficialTendonosisSupraspinatus
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={
                                    values.isSuperficialTendonosisSubscapularis
                                  }
                                  label="Tendonosis Subscapularis"
                                  name="isSuperficialTendonosisSubscapularis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialTendonosisSubscapularis",
                                      !values.isSuperficialTendonosisSubscapularis
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={
                                    values.isSuperficialRcTearSupraspinatus
                                  }
                                  label="RC Tear Supraspinatus"
                                  name="isSuperficialRcTearSupraspinatus"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialRcTearSupraspinatus",
                                      !values.isSuperficialRcTearSupraspinatus
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={
                                    values.isSuperficialRcTearSubscapularis
                                  }
                                  label="RC Tear Subscapularis"
                                  name="isSuperficialRcTearSubscapularis"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialRcTearSubscapularis",
                                      !values.isSuperficialRcTearSubscapularis
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={
                                    values.isSuperficialBicepTendonopathy
                                  }
                                  label="Bicep Tendonopathy"
                                  name="isSuperficialBicepTendonopathy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialBicepTendonopathy",
                                      !values.isSuperficialBicepTendonopathy
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={values.isSuperficialBicepTears}
                                  label="Bicep Tears"
                                  name="isSuperficialBicepTears"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialBicepTears",
                                      !values.isSuperficialBicepTears
                                    )
                                  }
                                />

                                <Checkbox
                                  checked={
                                    values.isSuperficialAcJointHypertrophy
                                  }
                                  label="AC Joint Hypertrophy"
                                  name="isSuperficialAcJointHypertrophy"
                                  onChange={() =>
                                    setFieldValue(
                                      "isSuperficialAcJointHypertrophy",
                                      !values.isSuperficialAcJointHypertrophy
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="p-6 text-center text-gray-500 italic">
                            Enable this body part to configure settings
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="flex gap-2 justify-end mt-4">
                    <button
                      className="bg-gray-100 px-4 py-2 rounded-md mr-2 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:pointer-events-none"
                      disabled={
                        isLoadingAddIssuerFacility ||
                        isLoadingUpdateIssuerFacility
                      }
                      type="button"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="bg-secondary text-white px-8 py-2 rounded-md mr-2 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:pointer-events-none"
                      disabled={
                        isLoadingAddIssuerFacility ||
                        isLoadingUpdateIssuerFacility
                      }
                      type="submit"
                    >
                      {issuerId ? "Update" : "Add"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </>
  )
}

export default CreateOrUpdateIssuerFacility

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, name, label }) => (
  <label className="flex items-center cursor-pointer">
    <div className="relative">
      <input
        checked={checked}
        className="sr-only"
        name={name}
        type="checkbox"
        onChange={onChange}
      />
      <div
        className={`block w-10 h-6 rounded-full ${checked ? "bg-secondary" : "bg-gray-300"}`}
      ></div>
      <div
        className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${checked ? "transform translate-x-full" : ""}`}
      ></div>
    </div>
    <span className="ml-3 text-lg font-medium">{label}</span>
  </label>
)

// Checkbox with Info Icon Component
const Checkbox = ({ name, label, checked, onChange }) => (
  <div>
    <label className="flex items-center space-x-2 cursor-pointer">
      <div className="flex items-center justify-center w-7 h-7 border border-gray-300 rounded-md">
        <input
          checked={checked}
          className="w-5 h-5 text-blue-600 rounded"
          name={name}
          type="checkbox"
          onChange={onChange}
        />
      </div>
      <span className="font-medium">{label}</span>
    </label>
  </div>
)
